import { useEffect, useState } from "react";
import { fmtDate, fmtDateTime } from "../lib/format";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Plus, FileText, Search, Bell, AlertTriangle, Download, X, UploadCloud } from "lucide-react";
import { InvoiceImportModal } from "../components/InvoiceImportModal";
import { NumberingCheck } from "../components/NumberingCheck";
import { toast } from "sonner";
import { VatModeBadge } from "../lib/vatMode";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

const STATUS = {
    vystavena: { label: "Vystavena", color: "bg-blue-100 text-blue-700 border-blue-300" },
    zaplacena: { label: "Zaplaceno", color: "bg-green-100 text-green-700 border-green-300" },
    stornovana: { label: "Stornováno", color: "bg-slate-200 text-slate-600 border-slate-400" },
};
const KIND = {
    zalohova: { label: "Záloha", color: "bg-amber-100 text-amber-800 border-amber-300" },
    konecna: { label: "Konečná", color: "bg-purple-100 text-purple-700 border-purple-300" },
    dobropis: { label: "Dobropis", color: "bg-red-100 text-red-700 border-red-300" },
    danovy_doklad: { label: "DD k platbě", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const MONTHS_CS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

export default function Invoices() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [onlyOverdue, setOnlyOverdue] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [expYear, setExpYear] = useState(CURRENT_YEAR);
    const [expMonth, setExpMonth] = useState("");
    const [expStatus, setExpStatus] = useState("");
    const [expVatMode, setExpVatMode] = useState("");
    const [exporting, setExporting] = useState(false);

    const load = () => api.get("/invoices").then((r) => { setItems(r.data); setLoading(false); });
    useEffect(() => { load(); }, []);

    const remind = async (id) => {
        try {
            const { data } = await api.post(`/invoices/${id}/reminder`, {});
            if (data.emailed) toast.success(`Upomínka odeslána e-mailem na ${data.emailed_to}`);
            else if (data.email_error) toast.warning(`Upomínka zaznamenána, e-mail se nepodařilo odeslat: ${data.email_error}`);
            else toast.success("Upomínka zaznamenána (e-mail neodeslán — chybí SMTP nebo e-mail klienta)");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Akce selhala"); }
    };

    const runExport = async (format) => {
        try {
            setExporting(true);
            const params = new URLSearchParams({ format });
            if (expYear) params.set("year", expYear);
            if (expMonth) params.set("month", expMonth);
            if (expStatus) params.set("status", expStatus);
            if (expVatMode) params.set("vat_mode", expVatMode);
            const res = await api.get(`/invoices/export?${params.toString()}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: res.headers["content-type"] });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const parts = [expVatMode === "pdp" ? "faktury-pdp" : "faktury"];
            if (expYear) parts.push(String(expYear));
            if (expMonth) parts.push(String(expMonth).padStart(2, "0"));
            a.download = `${parts.join("-")}.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Export ${format.toUpperCase()} stažen`);
            setExportOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export selhal");
        } finally {
            setExporting(false);
        }
    };

    const filtered = items.filter((b) => {
        if (onlyOverdue && !b.is_overdue) return false;
        return !q || b.title?.toLowerCase().includes(q.toLowerCase()) || b.client_name?.toLowerCase().includes(q.toLowerCase()) || b.number?.toLowerCase().includes(q.toLowerCase());
    });
    const overdueCount = items.filter((i) => i.is_overdue).length;

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="invoices-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Vyúčtování</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Faktury</h1>
                    <p className="text-sm text-slate-600 mt-2">Vystavte profesionální fakturu s českým QR kódem pro rychlou platbu.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to="/banka" data-testid="bank-link" className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm">Spárovat platby z banky →</Link>
                    <button onClick={() => setImportOpen(true)} data-testid="import-invoices-button"
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm">
                        <UploadCloud className="w-3.5 h-3.5" /> Importovat faktury
                    </button>
                    <button onClick={() => setExportOpen(true)} data-testid="export-invoices-button"
                            className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-4 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
                        <Download className="w-4 h-4" /> Export pro účetní
                    </button>
                    <Link to="/faktury/nova" data-testid="new-invoice-button"
                          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                        <Plus className="w-4 h-4" /> Nová faktura
                    </Link>
                </div>
            </div>
            <NumberingCheck compact onChanged={load} />

            <div className="bg-white border border-slate-200">
                <div className="flex items-center px-4 py-3 border-b border-slate-200 gap-3 flex-wrap">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input placeholder="Hledat fakturu…" value={q} onChange={(e) => setQ(e.target.value)}
                           className="flex-1 min-w-[140px] text-sm focus:outline-none bg-transparent" />
                    <button onClick={() => setOnlyOverdue(!onlyOverdue)} data-testid="filter-overdue"
                            className={`text-xs uppercase tracking-wider px-2 py-1 rounded-sm border whitespace-nowrap ${onlyOverdue ? "bg-red-50 text-red-700 border-red-300" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`}>
                        <AlertTriangle className="w-3 h-3 inline mr-1" /> Po splatnosti{overdueCount > 0 && ` (${overdueCount})`}
                    </button>
                    <span className="text-xs text-slate-400">{filtered.length} / {items.length}</span>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-slate-400 text-xs uppercase tracking-widest">Načítání…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">Žádné faktury</div>
                        <p className="text-sm text-slate-500 mt-1 mb-6">Vystavte první fakturu — můžete ji vytvořit i z hotového rozpočtu.</p>
                        <Link to="/faktury/nova" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                            <Plus className="w-4 h-4" /> Nová faktura
                        </Link>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-6 py-3 font-semibold">Číslo</th>
                                <th className="px-6 py-3 font-semibold">Klient</th>
                                <th className="px-6 py-3 font-semibold">Vystaveno</th>
                                <th className="px-6 py-3 font-semibold">Splatnost</th>
                                <th className="px-6 py-3 font-semibold">Stav</th>
                                <th className="px-6 py-3 font-semibold text-right">K úhradě</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((b, i) => {
                                const st = STATUS[b.status] || STATUS.vystavena;
                                return (
                                    <tr key={b.id} className={`border-b border-slate-100 hover:bg-orange-50/40 ${b.is_overdue ? "bg-red-50/30" : i % 2 ? "bg-slate-50/40" : ""}`}>
                                        <td className="px-6 py-3">
                                            <Link to={`/faktury/${b.id}`} className="font-mono text-xs text-orange-700 hover:underline">{b.number}</Link>
                                            {KIND[b.kind] && (
                                                <span data-testid={`kind-badge-${b.id}`} className={`ml-2 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${KIND[b.kind].color}`}>{KIND[b.kind].label}</span>
                                            )}
                                            <span className="ml-1.5"><VatModeBadge mode={b.vat_mode || "21"} testid={`vat-badge-${b.id}`} /></span>
                                            {b.needs_tax_doc && <Link to={`/faktury/${b.id}?dd=1`} data-testid={`needs-dd-${b.id}`} className="ml-1.5 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border bg-amber-50 text-amber-800 border-amber-400 hover:bg-amber-100">Chybí DD</Link>}
                                            {b.imported && <span data-testid={`imported-badge-${b.id}`} className="ml-1.5 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border bg-sky-50 text-sky-800 border-sky-300">Importováno</span>}
                                        </td>
                                        <td className="px-6 py-3"><Link to={`/faktury/${b.id}`} className="font-medium text-slate-900 hover:text-orange-600">{b.client_name || b.title}</Link></td>
                                        <td className="px-6 py-3 text-slate-600 text-xs">{fmtDate(b.issue_date)}</td>
                                        <td className="px-6 py-3 text-slate-600 text-xs">
                                            {fmtDate(b.due_date)}
                                            {b.is_overdue && (
                                                <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mt-0.5">
                                                    {b.days_overdue} dní po splatnosti
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-3">
                                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${st.color}`}>{st.label}</span>
                                            {b.reminder_count > 0 && (
                                                <div className="text-[10px] text-slate-500 mt-0.5">Upomínek: {b.reminder_count}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-right font-semibold">
                                            {b.kind === "danovy_doklad" ? <span className="text-emerald-800" data-testid={`dd-paid-${b.id}`}>uhrazeno {fmt(b.total)}</span> : fmt(b.amount_due ?? b.total)}
                                            {b.status === "vystavena" && (b.paid_total || 0) > 0 && <div className="text-[10px] text-amber-700 font-normal" data-testid={`partial-paid-${b.id}`}>částečně uhrazeno {fmt(b.paid_total)}</div>}
                                            {b.kind === "konecna" && (b.advance_total || 0) > 0 && (
                                                <div className="text-[10px] text-slate-500 font-normal">po odečtu záloh {fmt(b.advance_total)}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                            {b.is_overdue && (
                                                <button onClick={() => remind(b.id)} title="Zaznamenat upomínku" data-testid={`remind-${b.id}`}
                                                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-bold border border-red-300 text-red-700 hover:bg-red-50 rounded-sm">
                                                    <Bell className="w-3 h-3" /> Upomenout
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {importOpen && <InvoiceImportModal onClose={() => setImportOpen(false)} onDone={load} />}
            {exportOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="export-modal"
                     onClick={() => !exporting && setExportOpen(false)}>
                    <div className="bg-white border border-slate-200 shadow-2xl max-w-md w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                            <div>
                                <div className="label-eyebrow mb-1">Účetní</div>
                                <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Export faktur</h2>
                            </div>
                            <button onClick={() => setExportOpen(false)} className="text-slate-400 hover:text-slate-900" data-testid="export-modal-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Rok</label>
                                <select value={expYear} onChange={(e) => setExpYear(e.target.value)} data-testid="export-year-select"
                                        className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600">
                                    {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Měsíc (volitelně)</label>
                                <select value={expMonth} onChange={(e) => setExpMonth(e.target.value)} data-testid="export-month-select"
                                        className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600">
                                    <option value="">Celý rok</option>
                                    {MONTHS_CS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Stav (volitelně)</label>
                                <select value={expStatus} onChange={(e) => setExpStatus(e.target.value)} data-testid="export-status-select"
                                        className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600">
                                    <option value="">Všechny stavy</option>
                                    <option value="vystavena">Pouze vystavené</option>
                                    <option value="zaplacena">Pouze zaplacené</option>
                                    <option value="stornovana">Pouze stornované</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Režim DPH</label>
                                <select value={expVatMode} onChange={(e) => setExpVatMode(e.target.value)} data-testid="export-vat-mode-select"
                                        className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600">
                                    <option value="">Všechny faktury</option>
                                    <option value="pdp">Jen přenesená daňová povinnost (kontrolní hlášení)</option>
                                    <option value="21">Jen 21 %</option>
                                    <option value="12">Jen 12 %</option>
                                </select>
                            </div>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Export obsahuje: číslo faktury, data (vystavení, splatnost, DUZP), variabilní symbol, klienta včetně IČO/DIČ, základ DPH, sazbu, DPH, celkovou částku a stav.
                            </p>
                        </div>
                        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button onClick={() => runExport("csv")} disabled={exporting} data-testid="export-csv-button"
                                    className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 hover:bg-slate-50 px-4 py-2 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
                                <Download className="w-4 h-4" /> CSV
                            </button>
                            <button onClick={() => runExport("xlsx")} disabled={exporting} data-testid="export-xlsx-button"
                                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
                                <Download className="w-4 h-4" /> Excel (XLSX)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
